'use client';

import { useSession } from '@shared/auth';
import { Button } from '@shared/components/shadcn';
import { Verified } from '@shared/icons/system';
import { buildPlayerTrackerImpressionPayload, PLAYER_TRACKER_IMPRESSION, usePlayerTrackerServiceEvent } from '@shared/tracking';
import { capitalizeWords, NumberFormats as nf, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import type { DepositLimit, LimitPeriod } from '../../../types';
import { getTranslationKeyForPeriod } from '../../utils/utils';

type SuccessStepProps = {
  user: {
    firstName: string;
  };
  limitPeriod: LimitPeriod;
  variant?: 'create' | 'edit';
  limits: DepositLimit[];
  onClose: () => void;
  originalLimit: number;
  submittedValue?: number;
};

const SuccessStep = ({ user, limitPeriod, variant = 'edit', limits, onClose, originalLimit, submittedValue = 0 }: SuccessStepProps) => {
  const t = useTranslations('my_account.pages.set_deposit_limit.success_step');
  const tDepositLimits = useTranslations('my_account.safer_gambling.deposit_limits');
  const formatter = useCurrencyFormatter();
  const ptsTrackEvent = usePlayerTrackerServiceEvent();
  const { session } = useSession();
  const hasTrackedImpressionRef = useRef(false);

  // Ensure we have a valid submitted value
  const validSubmittedValue = submittedValue || 0;

  // submittedValue is in major units; convert to minor units for comparison and formatting
  const newDepositLimit = validSubmittedValue * 100;
  const isAdding = variant === 'create';

  // Filter out limits that don't have actual limit data (no limitValue means it's not a real limit)
  const activeLimits = limits.filter(limit => limit.limitValue !== undefined && limit.limitValue !== null && limit.limitValue > 0);

  // Check if user will have multiple unique (limitType, period) limits after adding this one
  // For "create", we want to know if the user will have more than one limit *after* this new one is added.
  const newLimitKey = `DEPOSIT:${limitPeriod}`;
  const activeLimitKeys = new Set(activeLimits.map(limit => `${limit.limitType}:${limit.period}`));

  // If we're creating and the new limit key doesn't already exist in active limits, add it
  if (isAdding && !activeLimitKeys.has(newLimitKey)) {
    activeLimitKeys.add(newLimitKey);
  }

  const willHaveMultipleLimits = isAdding && activeLimitKeys.size > 1;

  // Determine if this is an increase (only for edit variant)
  const isIncrease = newDepositLimit > originalLimit && variant === 'edit';
  const formattedAmount = formatter(newDepositLimit).format(nf.WITHOUT_DECIMALS).get();
  const periodKey = getTranslationKeyForPeriod(limitPeriod);
  const periodLabel = tDepositLimits(periodKey);
  const amountMessage = { period: periodLabel, amount: formattedAmount };
  let headingMessage: string;

  if (willHaveMultipleLimits) {
    headingMessage = t('multiple_limits_message', {
      period: t(`limit_period.${periodKey}`),
      amount: formattedAmount,
    });
  } else if (isIncrease) {
    headingMessage = t('increase_message', amountMessage);
  } else {
    headingMessage = t('decrease_message', amountMessage);
  }

  useEffect(() => {
    if (hasTrackedImpressionRef.current) return;

    const payload = buildPlayerTrackerImpressionPayload(PLAYER_TRACKER_IMPRESSION.DEPOSIT_LIMIT_SET_CONFIRMATION, session.profile);
    if (!payload) return;

    hasTrackedImpressionRef.current = true;
    ptsTrackEvent(payload);
  }, [ptsTrackEvent, session.profile]);

  return (
    <div
      className="flex flex-col h-full gap-5 md:mt-6"
      data-testid="deposit-limit-confirmation"
      data-impression={PLAYER_TRACKER_IMPRESSION.DEPOSIT_LIMIT_SET_CONFIRMATION}
      data-track-container={willHaveMultipleLimits ? 'Deposit Limit Multiple Confirmed' : 'Deposit Limit Confirmed'}
      data-track-type={capitalizeWords(limitPeriod)}
      data-track-value={newDepositLimit}
    >
      <Verified size="extra-extra-large" data-testid="verified-icon" />

      <h4>
        {isAdding ? (
          <>
            <span data-testid="success-title" data-track-privacy="Mask">
              {t('title_with_name', { name: user.firstName })}
            </span>
            {willHaveMultipleLimits ? ' ' : <br aria-hidden="true" />}
            <span data-testid="success-message">{headingMessage}</span>
          </>
        ) : (
          <span data-testid="success-message">{headingMessage}</span>
        )}
      </h4>

      {willHaveMultipleLimits ? (
        <p className="paragraph-explanatory" data-testid="success-multiple-limits-helper">
          {t('multiple_limits_helper')}
        </p>
      ) : null}

      <div className="mt-auto" data-track-container="Actions">
        <Button className="w-full" onClick={onClose} data-testid="success-continue-button" data-track-name="Done">
          {t('done', { default: 'Done' })}
        </Button>
      </div>
    </div>
  );
};

export { SuccessStep };
